import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useId, useState } from "react";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import type {
  ExamForm,
  ExamFormChoiceAnswer,
  ExamFormTestChoice,
} from "@rmh/defs";
import AdditionalCommentsInput from "../inputs/AdditionalCommentsInput";
import { getSubtype } from "../../utils/examFormUtils";
import TestFormContainer from "../inputs/TestFormContainer";

interface DisplayBinaryChoiceProps {
  examId: string;
  item: ExamFormTestChoice;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

function DisplayBinaryChoice({
  item,
  setExamState,
  examId,
  onInputChange,
  statusComponent,
  disabled,
}: DisplayBinaryChoiceProps){
  const testType = getSubtype(item);
  const controlId = useId();
  const defaultValueObject: ExamFormChoiceAnswer | null | undefined = (
    item.examFormChoiceAnswers ?? []
  ).find((answer) => answer?.isSelected);
  const defaultValue = defaultValueObject?.id ?? "";
  const [radioValue, setRadioValue] = useState<string>(defaultValue);
  const handleRadioChange = (index: number) => (): void => {
    const rowId = (item.examFormChoiceRows ?? [])[index]?.id ?? "";
    setRadioValue(rowId ? rowId : index.toString());
    if (setExamState) {
      const rowsCopy = [...(item.examFormChoiceRows ?? [])];
      const choiceAnswers = rowsCopy.map((row, i) => ({
        ...row,
        isSelected: i === index,
      }));
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestChoice;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.examFormChoiceAnswers =
          choiceAnswers as ExamFormChoiceAnswer[];
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };
  const handleCommentChange = (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestChoice;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.commentsText = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  return (
    <TestFormContainer
      id={controlId}
      statusSlot={statusComponent}
      title={item.testTitle ?? ""}
    >
      <RadioGroup aria-labelledby={controlId} className="flex w-full">
        {testType === "BinaryChoice" &&
          item.examFormChoiceRows?.map((row, index) => (
            <FormControlLabel
              control={<Radio checked={radioValue === row.id} />}
              disabled={disabled ? radioValue !== row.id : false}
              key={`radio_${row.id}`}
              label={row.rowTitle}
              onChange={handleRadioChange(index)}
              value={row.id}
            />
          ))}
        {testType === "YesNoChoice" && (
          <>
            <FormControlLabel
              control={<Radio checked={radioValue === "0"} />}
              disabled={disabled ? radioValue !== "0" : false}
              key={`radio_${item.testTitle}_yes`}
              label="Yes"
              onChange={handleRadioChange(0)}
              value="0"
            />
            <FormControlLabel
              control={<Radio checked={radioValue === "1"} />}
              disabled={disabled ? radioValue !== "1" : false}
              key={`radio_${item.testTitle}_no`}
              label="No"
              onChange={handleRadioChange(1)}
              value="1"
            />
          </>
        )}
        {testType === "PosNegChoice" && (
          <>
            <FormControlLabel
              control={<Radio checked={radioValue === "0"} />}
              disabled={disabled ? radioValue !== "0" : false}
              key={`radio_${item.testTitle}_pos`}
              label="Positive"
              onChange={handleRadioChange(0)}
              value="0"
            />
            <FormControlLabel
              control={<Radio checked={radioValue === "1"} />}
              disabled={disabled ? radioValue !== "1" : false}
              key={`radio_${item.testTitle}_neg`}
              label="Negative"
              onChange={handleRadioChange(1)}
              value="1"
            />
          </>
        )}
      </RadioGroup>
      {Boolean(item.comments) && (
        <AdditionalCommentsInput
          defaultValue={item.commentsText ?? ""}
          disabled={disabled}
          onChange={handleCommentChange}
        />
      )}
    </TestFormContainer>
  );
}

export default DisplayBinaryChoice;
